import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { GrokLogo } from "@/components/grok-logo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useState, useEffect, useRef, useMemo } from "react";
import { Home, UserPlus, Trash2, Shield, User as UserIcon, MessageSquare, Users, BarChart3, Upload, FileText, File, X, CheckCircle2, AlertCircle, Loader2, RefreshCw, ChevronDown, Edit, DollarSign, Lightbulb } from "lucide-react";
import { Link, Redirect } from "wouter";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "wouter";

type SafeUser = {
  id: string;
  username: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  department?: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureUrl?: string | null;
};

type AnalyticsData = {
  totalUsers: number;
  totalConversations: number;
  totalMessages: number;
  totalDocuments?: number;
  userActivity: {
    userId: string;
    username: string;
    conversationCount: number;
    messageCount: number;
    lastActivity: string | null;
  }[];
  dailyActivity: {
    date: string;
    messageCount: number;
    conversationCount: number;
  }[];
};

type CostUsageData = {
  summary: {
    model_family: string;
    model_type: string;
    task_type: string;
    token_count: number;
    input_tokens: number;
    output_tokens: number;
    cost_per_task: number;
    usage_percentage: number;
    pricing: { input: number; output: number };
    task_count: number;
  }[];
  totalCost: number;
  totalTokens: number;
  lastUpdated?: string;
};

type DocumentMetadata = {
  id: string;
  filename: string;
  uploadedBy: string;
  uploadedAt: string;
  contentLength: number;
  documentType: 'guideline' | 'exception';
  tags: string[];
  chunkCount: number;
  isProcessed: boolean;
  processingStatus?: 'pending' | 'parsing' | 'chunking' | 'indexing' | 'completed' | 'failed';
  processingError?: string;
};

type SelectedFileStatus = {
  file: File;
  status: 'queued' | 'uploading' | 'done' | 'error';
  error?: string;
};

// Document Status Component with processing indicator
function DocumentStatusBadge({ 
  chunkCount, 
  isProcessed,
  isProcessing = false,
  processingStatus
}: { 
  chunkCount: number; 
  isProcessed: boolean;
  isProcessing?: boolean;
  processingStatus?: 'pending' | 'parsing' | 'chunking' | 'indexing' | 'completed' | 'failed';
}) {
  // Show specific processing status
  if (processingStatus === 'parsing' || processingStatus === 'chunking' || processingStatus === 'indexing' || isProcessing) {
    const statusLabel = processingStatus === 'parsing' ? 'Parsing...' : 
                       processingStatus === 'chunking' ? 'Chunking...' : 
                       processingStatus === 'indexing' ? 'Indexing...' : 
                       'Processing...';
    
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">
          <Loader2 className="h-3 w-3 mr-1 animate-spin text-[#1a1a1a]" />
          {statusLabel}
        </Badge>
      </div>
    );
  }

  if (processingStatus === 'failed') {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">
          <AlertCircle className="h-3 w-3 mr-1 text-[#1a1a1a]" />
          Failed
        </Badge>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {isProcessed ? (
        <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">
          <CheckCircle2 className="h-3 w-3 mr-1 text-[#1a1a1a]" />
          {chunkCount} chunks
        </Badge>
      ) : (
        <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">
          <AlertCircle className="h-3 w-3 mr-1 text-[#1a1a1a]" />
          Pending
        </Badge>
      )}
    </div>
  );
}

// Document Actions Component (reprocess + delete)
function DocumentActions({ 
  documentId, 
  filename, 
  onProcessingChange 
}: { 
  documentId: string; 
  filename: string;
  onProcessingChange?: (isProcessing: boolean) => void;
}) {
  const { toast } = useToast();

  const reprocessMutation = useMutation({
    mutationFn: async () => {
      onProcessingChange?.(true);
      const res = await apiRequest('POST', `/api/admin/documents/${documentId}/reprocess`);
      return await res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Document reprocessed",
        description: `Successfully created ${data.chunks_created} chunks`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/documents'] });
      onProcessingChange?.(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Reprocessing failed",
        description: error.message,
        variant: "destructive",
      });
      onProcessingChange?.(false);
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (docId: string) => {
      await apiRequest('DELETE', `/api/admin/documents/${docId}`);
    },
    onSuccess: () => {
      toast({
        title: "Document deleted",
        description: "The document has been removed from the knowledge base",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/documents'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => reprocessMutation.mutate()}
        disabled={reprocessMutation.isPending}
        data-testid={`button-reprocess-${documentId}`}
        title="Reprocess this document"
      >
        {reprocessMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-[#1a1a1a]" />
        ) : (
          <RefreshCw className="h-4 w-4 text-[#1a1a1a]" />
        )}
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={deleteDocumentMutation.isPending}
            data-testid={`button-delete-document-${documentId}`}
          >
            <Trash2 className="h-4 w-4 text-[#1a1a1a]" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{filename}"? This will remove the document from the knowledge base and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDocumentMutation.mutate(documentId)}
              className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReprocessAllButton({ 
  documentsCount, 
  onProcessingChange 
}: { 
  documentsCount: number;
  onProcessingChange?: (isProcessing: boolean) => void;
}) {
  const { toast } = useToast();
  
  const reprocessAllMutation = useMutation({
    mutationFn: async () => {
      onProcessingChange?.(true);
      const res = await apiRequest("POST", "/api/admin/documents/reprocess-all");
      return res as unknown as { successful: number; failed: number; total: number; errors: string[]; message: string };
    },
    onSuccess: (data) => {
      toast({
        title: "Batch reprocessing complete",
        description: `Successfully reprocessed ${data.successful}/${data.total} documents`,
      });
      if (data.errors && data.errors.length > 0) {
        console.error("Reprocessing errors:", data.errors);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/admin/documents'] });
      onProcessingChange?.(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Batch reprocessing failed",
        description: error.message,
        variant: "destructive",
      });
      onProcessingChange?.(false);
    },
  });

  if (documentsCount === 0) {
    return null;
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          disabled={reprocessAllMutation.isPending}
          data-testid="button-reprocess-all"
        >
          {reprocessAllMutation.isPending ? (
            <>
              <Loader2 className="h-3 w-3 mr-2 animate-spin text-[#1a1a1a]" />
              Processing...
            </>
          ) : (
            <>
              <RefreshCw className="h-3 w-3 mr-2" />
              Reprocess All
            </>
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reprocess All Documents?</AlertDialogTitle>
          <AlertDialogDescription>
            This will reprocess all {documentsCount} documents and rebuild their vector embeddings in ChromaDB. This may take several minutes.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => reprocessAllMutation.mutate()}>
            Reprocess All
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Predefined product tags
const PRODUCT_TAGS = [
  'VA',
  'FHA',
  'DSCR',
  'Conforming',
  'Jumbo',
  'HELOC',
  'Flex Equity Income',
  'Bank Statement',
  'USDA',
  'VA IRRRL',
  'ONCF',
  'AUS INV+',
] as const;

// Document Tags Editor Component
function DocumentTagsEditor({ documentId, initialTags }: { documentId: string; initialTags: string[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tags, setTags] = useState<string[]>(initialTags || []);
  const { toast } = useToast();

  const updateTagsMutation = useMutation({
    mutationFn: async (newTags: string[]) => {
      const res = await apiRequest('PATCH', `/api/admin/documents/${documentId}/tags`, { tags: newTags });
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: "Tags updated",
        description: "Document tags have been updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/documents'] });
      setIsOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleTag = (tag: string) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSave = () => {
    updateTagsMutation.mutate(tags);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-auto p-1 hover:bg-[#f5f5f5] bg-transparent"
          data-testid={`button-edit-tags-${documentId}`}
        >
          {(initialTags && Array.isArray(initialTags) ? initialTags : []).filter(tag => tag && tag.trim()).length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {(initialTags && Array.isArray(initialTags) ? initialTags : []).filter(tag => tag && tag.trim()).map(tag => {
                // Clean up tags by removing brackets and trimming
                const cleanTag = tag.replace(/^\[|\]$/g, '').trim();
                return (
                  <Badge key={tag} variant="outline" className="text-xs border-[#d0d0d0] text-[#1a1a1a] bg-[#f5f5f5]">
                    {cleanTag}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <span className="text-xs text-[#1a1a1a]">+ Add tags</span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border border-[#d0d0d0]">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a1a]">Edit Product Tags</DialogTitle>
          <DialogDescription className="text-[#1a1a1a]">
            Select the product types that apply to this document
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PRODUCT_TAGS.map(tag => (
              <Button
                key={tag}
                type="button"
                variant={tags.includes(tag) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleTag(tag)}
                disabled={updateTagsMutation.isPending}
                data-testid={`button-tag-option-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs ${
                  tags.includes(tag) 
                    ? "border border-[#1a1a1a] bg-[#f5f5f5] text-[#1a1a1a] hover:bg-[#e8e8e8]" 
                    : "border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:bg-[#f5f5f5]"
                }`}
              >
                {tag}
              </Button>
            ))}
          </div>
          {tags.length > 0 && (
            <div className="text-xs text-[#1a1a1a]">
              Selected: {tags.join(', ')}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsOpen(false)} 
            disabled={updateTagsMutation.isPending}
            className="border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:bg-[#f5f5f5]"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={updateTagsMutation.isPending}
            className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
          >
            {updateTagsMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin text-[#1a1a1a]" />
                Saving...
              </>
            ) : (
              'Save Tags'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Document Upload Component
function DocumentUpload({ onUploadComplete }: { onUploadComplete: () => void }) {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentType, setDocumentType] = useState<'guideline' | 'exception'>('guideline'); // State for document type
  const [selectedTags, setSelectedTags] = useState<string[]>([]); // State for product tags
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pasteContent, setPasteContent] = useState('');
  const [pasteFilename, setPasteFilename] = useState('');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      console.log('[Upload] Starting upload for:', file.name, file.size, file.type);
      const formData = new FormData();
      formData.append("file", file);
      formData.append('documentType', documentType); // Append selected document type
      formData.append('tags', JSON.stringify(selectedTags)); // Append selected tags

      console.log('[Upload] FormData created, sending request');
      const res = await apiRequest("POST", "/api/admin/documents", formData);
      console.log('[Upload] Response received:', res.status);
      const data = await res.json();
      console.log('[Upload] Upload successful:', data);
      return data;
    },
    onSuccess: (data) => {
      // Status polling is now handled in handleMultiUpload after upload completes
      // This ensures we only poll once per document
    },
    onError: (error: Error) => {
      console.error('[Upload] Upload failed:', error);
    },
  });

  // Paste document mutation
  const pasteDocumentMutation = useMutation({
    mutationFn: async ({ content, filename }: { content: string; filename: string }) => {
      console.log('[Paste] Starting paste for:', filename, 'Content length:', content.length);
      const res = await apiRequest("POST", "/api/admin/documents/paste", {
        content,
        filename,
        documentType,
        tags: selectedTags
      });
      console.log('[Paste] Response received:', res.status);
      const data = await res.json();
      console.log('[Paste] Paste successful:', data);
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Paste Successful",
        description: `Processing ${data.filename}...`,
      });
      setShowPasteDialog(false);
      setPasteContent('');
      setPasteFilename('');
      // Start status polling if document is being processed
      if (data.id && (data.processingStatus === 'pending' || data.processingStatus === 'parsing' || data.processingStatus === 'chunking' || data.processingStatus === 'indexing')) {
        startStatusPolling(data.id);
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/documents"] });
      onUploadComplete();
    },
    onError: (error: Error) => {
      console.error('[Paste] Paste failed:', error);
      toast({
        title: "Paste Failed",
        description: error.message || 'Failed to paste document',
        variant: "destructive",
      });
    },
  });

  // Poll document status until processing completes
  const startStatusPolling = (documentId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const res = await apiRequest("GET", `/api/admin/documents/${documentId}/status`);
        const status = await res.json();
        
        // Update documents query cache with new status
        queryClient.setQueryData<DocumentMetadata[]>(["/api/admin/documents"], (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(doc => 
            doc.id === documentId 
              ? { 
                  ...doc, 
                  processingStatus: status.processingStatus,
                  chunkCount: status.chunkCount,
                  isProcessed: status.isProcessed,
                  processingError: status.processingError
                }
              : doc
          );
        });

        // Stop polling if processing is complete or failed
        if (status.processingStatus === 'completed' || status.processingStatus === 'failed') {
          clearInterval(pollInterval);
          queryClient.invalidateQueries({ queryKey: ["/api/admin/documents"] });
        }
      } catch (error) {
        console.error('[Status Poll] Error checking status:', error);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes (max processing time)
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[File Selection] onChange triggered', e.target.files);
    const files = Array.from(e.target.files || []);
    console.log('[File Selection] Files selected:', files.length);

    if (files.length === 0) {
      console.log('[File Selection] No files selected, returning');
      return;
    }

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.txt', '.csv', '.json', '.jsonl'];

    // Ensure files is an array before calling filter
    if (!Array.isArray(files)) {
      console.error('[File Selection] files is not an array:', files);
      return;
    }

    const newFiles: SelectedFileStatus[] = files
      .filter((file) => {
        // Check file size
        if (file.size > MAX_SIZE) {
          console.log('[File Selection] File too large:', file.name);
          toast({
            title: "File too large",
            description: `${file.name} exceeds 10MB limit`,
            variant: "destructive",
          });
          return false;
        }
        // Check file type
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ALLOWED_TYPES.includes(ext)) {
          console.log('[File Selection] Invalid file type:', file.name, ext);
          toast({
            title: "Invalid file type",
            description: `${file.name} is not a supported format`,
            variant: "destructive",
          });
          return false;
        }
        // Check for duplicates
        const isDuplicate = selectedFiles.some(
          (sf) => sf.file.name === file.name && sf.file.size === file.size
        );
        if (isDuplicate) {
          console.log('[File Selection] Duplicate file:', file.name);
          return false;
        }
        console.log('[File Selection] File accepted:', file.name);
        return true;
      })
      .map((file) => ({ file, status: 'queued' as const }));

    console.log('[File Selection] Adding files to state:', newFiles.length);
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    toast({
      title: "Files selected",
      description: `${newFiles.length} file(s) ready to upload`,
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMultiUpload = async () => {
    console.log('[Multi Upload] Button clicked, selectedFiles:', selectedFiles);
    const queuedFiles = selectedFiles.filter((sf) => sf.status === 'queued');
    console.log('[Multi Upload] Queued files:', queuedFiles.length);

    if (queuedFiles.length === 0) {
      console.log('[Multi Upload] No queued files to upload');
      toast({
        title: "No files to upload",
        description: "Please select files to upload.",
        variant: "destructive",
      });
      return;
    }

    console.log('[Multi Upload] Starting upload of', queuedFiles.length, 'files');
    setIsUploading(true);
    const BATCH_SIZE = 3;
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < queuedFiles.length; i += BATCH_SIZE) {
      const batch = queuedFiles.slice(i, i + BATCH_SIZE);

      // Ensure batch is an array before calling map
      if (!Array.isArray(batch)) {
        console.error('[Multi Upload] batch is not an array:', batch);
        continue;
      }

      const promises = batch.map(async (fileStatus) => {
        const fileIndex = selectedFiles.findIndex((sf) => sf.file === fileStatus.file);

        setSelectedFiles((prev) => {
          const updated = [...prev];
          updated[fileIndex] = { ...updated[fileIndex], status: 'uploading' };
          return updated;
        });

        try {
          const result = await uploadDocumentMutation.mutateAsync(fileStatus.file);

          // Mark as done immediately after upload (processing happens in background)
          setSelectedFiles((prev) => {
            const updated = [...prev];
            updated[fileIndex] = { ...updated[fileIndex], status: 'done' };
            return updated;
          });
          
          // Start status polling if document is being processed
          if (result.id && (result.processingStatus === 'pending' || result.processingStatus === 'parsing' || result.processingStatus === 'chunking' || result.processingStatus === 'indexing')) {
            startStatusPolling(result.id);
          }
          
          successCount++;
          return { success: true };
        } catch (error) {
          setSelectedFiles((prev) => {
            const updated = [...prev];
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: 'error',
              error: error instanceof Error ? error.message : 'Upload failed',
            };
            return updated;
          });
          errorCount++;
          return { success: false };
        }
      });

      await Promise.allSettled(promises);
    }

    setIsUploading(false);

    queryClient.invalidateQueries({ queryKey: ["/api/admin/documents"] });

    if (successCount > 0 && errorCount === 0) {
      toast({
        title: "Upload complete",
        description: `${successCount} ${successCount === 1 ? 'file' : 'files'} uploaded successfully.`,
      });
      setSelectedFiles((prev) => prev.filter((sf) => sf.status === 'error'));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onUploadComplete();
    } else if (successCount > 0 && errorCount > 0) {
      toast({
        title: "Upload partially complete",
        description: `${successCount} succeeded, ${errorCount} failed. Failed files are kept for retry.`,
        variant: "destructive",
      });
      setSelectedFiles((prev) => prev.filter((sf) => sf.status === 'error'));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onUploadComplete();
    } else {
      toast({
        title: "Upload failed",
        description: `All ${errorCount} ${errorCount === 1 ? 'file' : 'files'} failed to upload.`,
        variant: "destructive",
      });
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="documentType" className="text-[#1a1a1a]">Document Type</Label>
        <Select value={documentType} onValueChange={(value: 'guideline' | 'exception') => setDocumentType(value)}>
          <SelectTrigger id="documentType" className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-sm text-[#1a1a1a]">
            <SelectValue placeholder="Select document type..." />
          </SelectTrigger>
          <SelectContent className="bg-white border border-[#d0d0d0]">
            <SelectItem value="guideline">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#1a1a1a]">Guideline</span>
                <span className="text-xs text-[#1a1a1a]">(Strict Rules)</span>
              </div>
            </SelectItem>
            <SelectItem value="exception">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#1a1a1a]">Exception</span>
                <span className="text-xs text-[#1a1a1a]">(Flexible Criteria)</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Product Tags (Optional)</Label>
        <div className="flex flex-wrap gap-2">
          {PRODUCT_TAGS.map(tag => (
            <Button
              key={tag}
              type="button"
              variant={selectedTags.includes(tag) ? "default" : "outline"}
              size="sm"
              onClick={() => toggleTag(tag)}
              disabled={isUploading}
              data-testid={`button-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-xs"
            >
              {tag}
            </Button>
          ))}
        </div>
        {selectedTags.length > 0 && (
          <div className="text-xs text-[#1a1a1a]">
            Selected: {selectedTags.join(', ')}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-[#1a1a1a]">Upload Files or Paste Text</Label>
        <div className="flex gap-2">
          <Input
            type="file"
            accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.csv,.json,.jsonl"
            multiple
            onChange={handleFileSelect}
            ref={fileInputRef}
            disabled={isUploading}
            data-testid="input-document-file"
            className="flex-1"
          />
          <Button
            type="button"
            onClick={() => setShowPasteDialog(true)}
            disabled={isUploading || pasteDocumentMutation.isPending}
            className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] whitespace-nowrap"
            data-testid="button-paste-text"
          >
            <FileText className="h-4 w-4 mr-2" />
            Paste Text
          </Button>
        </div>
        <p className="text-xs text-[#1a1a1a] opacity-70">
          Click "Paste Text" to paste document content directly instead of uploading a file
        </p>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          {(selectedFiles && Array.isArray(selectedFiles) ? selectedFiles : []).map((fileStatus, index) => (
            <div
              key={`${fileStatus.file.name}-${index}`}
                  className="flex items-center gap-3 p-2 bg-[#f5f5f5] border border-[#d0d0d0] rounded-md"
              data-testid={`row-selected-file-${index}`}
            >
              {fileStatus.status === 'queued' && (
                <FileText className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
              )}
              {fileStatus.status === 'uploading' && (
                <Loader2 className="h-4 w-4 text-[#1a1a1a] animate-spin flex-shrink-0" />
              )}
              {fileStatus.status === 'done' && (
                <CheckCircle2 className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
              )}
              {fileStatus.status === 'error' && (
                <AlertCircle className="h-4 w-4 text-[#1a1a1a] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-[#1a1a1a]">
                  {fileStatus.file.name}
                </div>
                <div className="text-xs text-[#1a1a1a]">
                  {(fileStatus.file.size / 1024).toFixed(2)} KB
                  {fileStatus.status === 'error' && fileStatus.error && (
                    <span className="text-[#1a1a1a] ml-2">- {fileStatus.error}</span>
                  )}
                </div>
              </div>
              {(fileStatus.status === 'queued' || fileStatus.status === 'error') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(index)}
                  disabled={isUploading}
                  data-testid={`button-remove-file-${index}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-[#1a1a1a]">
        Accepted formats: PDF, DOCX, Excel, TXT, CSV, JSON, JSONL (max 10MB per file)
      </div>

      {selectedFiles.length > 0 && (
        <Button
          onClick={handleMultiUpload}
          disabled={isUploading || selectedFiles.filter(sf => sf.status === 'queued').length === 0}
          className="w-full border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] rounded-md"
          data-testid="button-upload-documents"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin text-[#1a1a1a]" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload {selectedFiles.filter(sf => sf.status === 'queued').length} {selectedFiles.filter(sf => sf.status === 'queued').length === 1 ? 'Document' : 'Documents'}
            </>
          )}
        </Button>
      )}

      {/* Paste Text Dialog */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border border-[#d0d0d0] bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Paste Document Content</DialogTitle>
            <DialogDescription className="text-[#1a1a1a]">
              Paste your document text directly. It will be processed the same way as an uploaded file (chunked and embedded).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="paste-filename" className="text-[#1a1a1a]">Filename</Label>
              <Input
                id="paste-filename"
                value={pasteFilename}
                onChange={(e) => setPasteFilename(e.target.value)}
                placeholder="e.g., HELOC_Guidelines_2024"
                className="border border-[#d0d0d0] bg-white text-[#1a1a1a]"
                disabled={pasteDocumentMutation.isPending}
              />
              <p className="text-xs text-[#1a1a1a] opacity-70">
                .txt extension will be added automatically
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paste-content" className="text-[#1a1a1a]">Content</Label>
              <Textarea
                id="paste-content"
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                placeholder="Paste your document content here..."
                className="min-h-[400px] font-mono text-sm border border-[#d0d0d0] bg-white text-[#1a1a1a]"
                disabled={pasteDocumentMutation.isPending}
              />
              <p className="text-xs text-[#1a1a1a] opacity-70">
                {pasteContent.length.toLocaleString()} characters
              </p>
            </div>
            {selectedTags.length > 0 && (
              <div className="text-xs text-[#1a1a1a]">
                Tags: {selectedTags.join(', ')}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasteDialog(false);
                setPasteContent('');
                setPasteFilename('');
              }}
              disabled={pasteDocumentMutation.isPending}
              className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!pasteFilename.trim()) {
                  toast({
                    title: "Filename Required",
                    description: "Please enter a filename",
                    variant: "destructive",
                  });
                  return;
                }
                if (!pasteContent.trim()) {
                  toast({
                    title: "Content Required",
                    description: "Please paste some content",
                    variant: "destructive",
                  });
                  return;
                }
                pasteDocumentMutation.mutate({
                  content: pasteContent,
                  filename: pasteFilename.trim()
                });
              }}
              disabled={pasteDocumentMutation.isPending || !pasteFilename.trim() || !pasteContent.trim()}
              className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
            >
              {pasteDocumentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Paste & Process
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Cost Usage Overview Collapsible Component
function CostUsageOverviewCollapsible({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) {
  const { data: costUsage } = useQuery<CostUsageData>({
    queryKey: ["/api/admin/cost-usage"],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/cost-usage');
      return await res.json();
    },
    refetchInterval: 10000,
    retry: false,
  });

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="mt-8 rounded-lg border border-[#e0e0e0] bg-white shadow-sm">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-[#f5f5f5] transition-colors" style={{ textAlign: 'left' }}>
            <div className="flex items-center justify-between">
              <div style={{ textAlign: 'left', flex: 1 }}>
                <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>Model Usage & Costs</CardTitle>
                <CardDescription className="text-[#666666] text-sm leading-6 mt-1" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                  Monitor AI model usage, token consumption, and operational costs
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {costUsage?.lastUpdated && (
                  <div className="text-xs text-[#666666]" style={{ textAlign: 'right' }}>
                    Updated: {new Date(costUsage.lastUpdated).toLocaleTimeString()}
                  </div>
                )}
                <ChevronDown 
                  className={`h-5 w-5 transition-transform duration-200 text-[#1a1a1a] ${
                    isOpen ? "transform rotate-180" : ""
                  }`}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent>
            <CostUsageOverview />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Cost Usage Overview Component
function CostUsageOverview() {
  const { data: costUsage, isLoading: costUsageLoading, error: costUsageError } = useQuery<CostUsageData>({
    queryKey: ["/api/admin/cost-usage"],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/cost-usage');
      return await res.json();
    },
    refetchInterval: 10000, // Refetch every 10 seconds for real-time updates
    retry: false,
  });

  if (costUsageLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#1a1a1a]" />
        <span className="ml-2 text-[#1a1a1a]">Loading cost usage data...</span>
      </div>
    );
  }

  if (costUsageError) {
    console.error('[Cost Usage] Error loading data:', costUsageError);
    return (
      <div className="text-center py-8 text-[#1a1a1a]">
        <AlertCircle className="h-6 w-6 mx-auto mb-2 text-[#1a1a1a]" />
        <p className="font-semibold mb-1">Failed to load cost usage data</p>
        <p className="text-sm text-[#666666] mt-2">
          {costUsageError instanceof Error ? costUsageError.message : 'Unknown error'}
        </p>
        <p className="text-xs text-[#666666] mt-2">
          Make sure the model_usage table exists in your database. Run the migration if needed.
        </p>
      </div>
    );
  }

  const summary = costUsage?.summary || [];
  const totalCost = costUsage?.totalCost || 0;
  const totalTokens = costUsage?.totalTokens || 0;

  return (
    <div>
        {summary.length === 0 ? (
          <div className="text-center py-8 text-[#1a1a1a]">
            <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-20" />
            <p>No usage data available yet</p>
            <p className="text-xs mt-1 text-[#666666]">Usage data will appear here as models are used</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-[#1a1a1a]">Total Cost</div>
                  <BarChart3 className="h-5 w-5 text-[#1a1a1a]" />
                </div>
                <div className="text-3xl font-bold text-[#1a1a1a]">
                  {`$${totalCost.toFixed(4)}`}
                </div>
                <div className="text-xs text-[#666666] mt-1">All-time spending</div>
              </div>
              <div className="p-5 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-[#1a1a1a]">Total Tokens</div>
                  <FileText className="h-5 w-5 text-[#1a1a1a]" />
                </div>
                <div className="text-3xl font-bold text-[#1a1a1a]">
                  {totalTokens.toLocaleString()}
                </div>
                <div className="text-xs text-[#666666] mt-1">Tokens consumed</div>
              </div>
              <div className="p-5 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-[#1a1a1a]">Avg Cost/1K Tokens</div>
                  <Users className="h-5 w-5 text-[#1a1a1a]" />
                </div>
                <div className="text-3xl font-bold text-[#1a1a1a]">
                  {`$${totalTokens > 0 ? ((totalCost * 1000) / totalTokens).toFixed(4) : '0.0000'}`}
                </div>
                <div className="text-xs text-[#666666] mt-1">Cost efficiency</div>
              </div>
            </div>

            {/* Detailed Usage Table */}
            <div className="border border-[#d0d0d0] rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f5f5f5]">
                    <TableHead className="text-[#1a1a1a] text-sm font-semibold">Model</TableHead>
                    <TableHead className="text-[#1a1a1a] text-sm font-semibold">Task Type</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Pricing (per 1M tokens)</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Input Tokens</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Output Tokens</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Total Tokens</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Tasks</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Total Cost</TableHead>
                    <TableHead className="text-right text-[#1a1a1a] text-sm font-semibold">Usage %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.map((item, index) => (
                    <TableRow 
                      key={`${item.model_family}-${item.model_type}-${item.task_type}-${index}`}
                      className="hover:bg-[#f9f9f9] border-b border-[#d0d0d0]"
                    >
                      <TableCell className="py-4">
                        <div>
                          <div className="font-semibold text-[#1a1a1a] text-sm">
                            {item.model_type}
                          </div>
                          <div className="text-xs text-[#666666] mt-0.5">
                            {item.model_family}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className="border-[#d0d0d0] text-[#1a1a1a] bg-[#f5f5f5]"
                        >
                          {item.task_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-xs text-[#666666]">
                          <div>Input: {`$${item.pricing?.input || 0}/1M`}</div>
                          <div>Output: {`$${item.pricing?.output || 0}/1M`}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#1a1a1a] font-medium">
                        {(item.input_tokens || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#1a1a1a] font-medium">
                        {(item.output_tokens || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#1a1a1a] font-medium">
                        {item.token_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#1a1a1a]">
                        {item.task_count || 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-[#1a1a1a] font-semibold">
                        {`$${item.cost_per_task.toFixed(4)}`}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-[#e0e0e0] rounded-full h-2">
                            <div 
                              className="bg-[#1a1a1a] h-2 rounded-full" 
                              style={{ width: `${Math.min(item.usage_percentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-[#1a1a1a] tabular-nums w-12 text-right">
                            {item.usage_percentage.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Model Summary by Family */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {['Claude', 'GPT'].map(family => {
                const familyData = summary.filter(s => s.model_family === family);
                if (familyData.length === 0) return null;
                
                const familyCost = familyData.reduce((sum, item) => sum + item.cost_per_task, 0);
                const familyTokens = familyData.reduce((sum, item) => sum + item.token_count, 0);
                const familyPercentage = totalTokens > 0 ? (familyTokens * 100) / totalTokens : 0;
                
                return (
                  <Card key={family} className="border border-[#d0d0d0] bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-[#1a1a1a]">{family} Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666666]">Total Cost:</span>
                          <span className="font-semibold text-[#1a1a1a]">{`$${familyCost.toFixed(4)}`}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666666]">Total Tokens:</span>
                          <span className="font-semibold text-[#1a1a1a]">{familyTokens.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#666666]">Usage:</span>
                          <span className="font-semibold text-[#1a1a1a]">{familyPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="pt-2 border-t border-[#d0d0d0]">
                          <div className="text-xs text-[#666666] mb-1">Models Used:</div>
                          <div className="flex flex-wrap gap-1">
                            {[...new Set(familyData.map(d => d.model_type))].map(model => (
                              <Badge key={model} variant="outline" className="text-xs border-[#d0d0d0]">
                                {model}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
}

const DEPARTMENT_OPTIONS = [
  "Sales",
  "Processing",
  "HR",
  "Underwriting",
  "Operations",
  "IT",
  "Finance",
  "Compliance",
  "Management",
  "Other"
];

export default function AdminPage() {
  const { user, isLoading: authLoading, error: authError } = useAuth();
  const { toast } = useToast();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SafeUser | null>(null);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [newDepartment, setNewDepartment] = useState("Sales");
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editDepartment, setEditDepartment] = useState("Sales");

  const { data: users = [], isLoading, error: usersError } = useQuery<SafeUser[]>({
    queryKey: ["/api/admin/users"],
    enabled: !!(user?.isAdmin || (user as any)?.is_admin),
    retry: false,
  });

  // Sort users alphabetically by username
  const sortedUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) return [];
    return [...users].sort((a, b) => {
      const usernameA = (a.username || '').toLowerCase();
      const usernameB = (b.username || '').toLowerCase();
      return usernameA.localeCompare(usernameB);
    });
  }, [users]);

  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = useQuery<AnalyticsData>({
    queryKey: ["/api/admin/analytics"],
    enabled: !!(user?.isAdmin || (user as any)?.is_admin),
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false,
    select: (data) => {
      // Ensure analytics data is properly formatted
      if (!data || typeof data !== 'object') {
        console.error('Analytics API returned invalid data:', data);
        return {
          totalUsers: 0,
          totalConversations: 0,
          totalMessages: 0,
          totalDocuments: 0,
          userActivity: [],
          dailyActivity: []
        };
      }
      
      // Ensure arrays are properly initialized
      const safeData = {
        ...data,
        userActivity: Array.isArray(data.userActivity) ? data.userActivity : [],
        dailyActivity: Array.isArray(data.dailyActivity) ? data.dailyActivity : []
      };
      
      return safeData;
    }
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData: { username: string; password: string; firstName?: string; lastName?: string; isAdmin: boolean; department: string }) => {
      const res = await apiRequest("POST", "/api/admin/users", userData);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setDialogOpen(false);
      setNewUsername("");
      setNewPassword("");
      setNewFirstName("");
      setNewLastName("");
      setNewIsAdmin(false);
      setNewDepartment("Sales");
      toast({
        title: "User created",
        description: "The new user has been created successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "User deleted",
        description: "The user has been removed successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, username, password, firstName, lastName, isAdmin, department }: { id: string; username?: string; password?: string; firstName?: string; lastName?: string; isAdmin?: boolean; department?: string }) => {
      const updateData: any = {};
      if (username) updateData.username = username;
      if (password && password.trim() !== '') updateData.password = password;
      if (firstName !== undefined) updateData.firstName = firstName.trim() || null;
      if (lastName !== undefined) updateData.lastName = lastName.trim() || null;
      if (isAdmin !== undefined) updateData.isAdmin = isAdmin;
      if (department !== undefined) updateData.department = department;
      
      const res = await apiRequest("PUT", `/api/admin/users/${id}`, updateData);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setEditDialogOpen(false);
      setEditingUser(null);
      setEditUsername("");
      setEditPassword("");
      setEditFirstName("");
      setEditLastName("");
      setEditIsAdmin(false);
      setEditDepartment("Sales");
      toast({
        title: "User updated",
        description: "The user has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Document library state and queries
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentProcessingStates, setDocumentProcessingStates] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { data: documents = [], isLoading: documentsLoading, error: documentsError } = useQuery<DocumentMetadata[]>({
    queryKey: ["/api/admin/documents"],
    enabled: !!(user?.isAdmin || (user as any)?.is_admin),
    retry: false,
    select: (data) => {
      // Ensure we always return an array, even if the API returns something else
      if (!Array.isArray(data)) {
        console.error('Documents API returned non-array data:', data);
        return [];
      }
      return data;
    }
  });

  // Debug logging for documents data
  useEffect(() => {
    if (documents && documents.length > 0) {
      console.log('[Admin] Documents data:', documents);
      documents.forEach((doc, index) => {
        console.log(`[Admin] Document ${index}:`, {
          filename: doc.filename,
          chunkCount: doc.chunkCount,
          isProcessed: doc.isProcessed
        });
      });
    }
  }, [documents]);

  const uploadDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      console.log('[Upload] Starting upload for:', file.name, file.size, file.type);
      const formData = new FormData();
      formData.append("file", file);
      console.log('[Upload] FormData created, sending request');
      const res = await apiRequest("POST", "/api/admin/documents", formData);
      console.log('[Upload] Response received:', res.status);
      const data = await res.json();
      console.log('[Upload] Upload successful:', data);
      return data;
    },
    onError: (error: Error) => {
      console.error('[Upload] Upload failed:', error);
    },
  });

  // System prompt state and queries
  const [systemPrompt, setSystemPrompt] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [isSystemPromptOpen, setIsSystemPromptOpen] = useState(false);

  // Collapsible sections state
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isCostUsageOpen, setIsCostUsageOpen] = useState(false);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isDocumentLibraryOpen, setIsDocumentLibraryOpen] = useState(false);

  const { data: promptData, isLoading: promptLoading, error: promptError } = useQuery<{ value: string; default: string }>({
    queryKey: ["/api/admin/system-prompt"],
    enabled: !!(user?.isAdmin || (user as any)?.is_admin),
    retry: false,
  });

  useEffect(() => {
    if (promptData) {
      setSystemPrompt(promptData.value);
      setDefaultPrompt(promptData.default);
    }
  }, [promptData]);

  // Add error logging after all queries are declared
  useEffect(() => {
    if (authError) {
      console.error('Auth error:', authError);
    }
    if (usersError) {
      console.error('Users query error:', usersError);
    }
    if (analyticsError) {
      console.error('Analytics query error:', analyticsError);
    }
    if (documentsError) {
      console.error('Documents query error:', documentsError);
    }
    if (promptError) {
      console.error('Prompt query error:', promptError);
    }
  }, [authError, usersError, analyticsError, documentsError, promptError]);

  const updatePromptMutation = useMutation({
    mutationFn: async (value: string) => {
      await apiRequest("PUT", "/api/admin/system-prompt", { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/system-prompt"] });
      toast({
        title: "System prompt updated",
        description: "Grok AI will now use your custom instructions.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update prompt",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSavePrompt = () => {
    if (!systemPrompt.trim()) {
      toast({
        title: "Prompt cannot be empty",
        description: "Please enter a system prompt.",
        variant: "destructive",
      });
      return;
    }
    updatePromptMutation.mutate(systemPrompt);
  };

  const handleResetPrompt = () => {
    setSystemPrompt(defaultPrompt);
    toast({
      title: "Prompt reset",
      description: "System prompt has been reset to default.",
    });
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[File Selection] onChange triggered', e.target.files);
    const files = Array.from(e.target.files || []);
    console.log('[File Selection] Files selected:', files.length);

    if (files.length === 0) {
      console.log('[File Selection] No files selected, returning');
      return;
    }

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['.pdf', '.docx', '.doc', '.xlsx', '.xls', '.txt', '.csv', '.json', '.jsonl'];

    // Ensure files is an array before calling filter
    if (!Array.isArray(files)) {
      console.error('[File Selection] files is not an array:', files);
      return;
    }

    const newFiles: SelectedFileStatus[] = files
      .filter((file) => {
        // Check file size
        if (file.size > MAX_SIZE) {
          console.log('[File Selection] File too large:', file.name);
          toast({
            title: "File too large",
            description: `${file.name} exceeds 10MB limit`,
            variant: "destructive",
          });
          return false;
        }
        // Check file type
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ALLOWED_TYPES.includes(ext)) {
          console.log('[File Selection] Invalid file type:', file.name, ext);
          toast({
            title: "Invalid file type",
            description: `${file.name} is not a supported format`,
            variant: "destructive",
          });
          return false;
        }
        // Check for duplicates
        const isDuplicate = selectedFiles.some(
          (sf) => sf.file.name === file.name && sf.file.size === file.size
        );
        if (isDuplicate) {
          console.log('[File Selection] Duplicate file:', file.name);
          return false;
        }
        console.log('[File Selection] File accepted:', file.name);
        return true;
      })
      .map((file) => ({ file, status: 'queued' as const }));

    console.log('[File Selection] Adding files to state:', newFiles.length);
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    toast({
      title: "Files selected",
      description: `${newFiles.length} file(s) ready to upload`,
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMultiUpload = async () => {
    console.log('[Multi Upload] Button clicked, selectedFiles:', selectedFiles);
    const queuedFiles = selectedFiles.filter((sf) => sf.status === 'queued');
    console.log('[Multi Upload] Queued files:', queuedFiles.length);

    if (queuedFiles.length === 0) {
      console.log('[Multi Upload] No queued files to upload');
      toast({
        title: "No files to upload",
        description: "Please select files to upload.",
        variant: "destructive",
      });
      return;
    }

    console.log('[Multi Upload] Starting upload of', queuedFiles.length, 'files');
    setIsUploading(true);
    const BATCH_SIZE = 3; // Upload 3 files at a time
    let successCount = 0;
    let errorCount = 0;

    // Process files in batches
    for (let i = 0; i < queuedFiles.length; i += BATCH_SIZE) {
      const batch = queuedFiles.slice(i, i + BATCH_SIZE);

      // Ensure batch is an array before calling map
      if (!Array.isArray(batch)) {
        console.error('[Multi Upload] batch is not an array:', batch);
        continue;
      }

      const promises = batch.map(async (fileStatus) => {
        const fileIndex = selectedFiles.findIndex((sf) => sf.file === fileStatus.file);

        // Update status to uploading
        setSelectedFiles((prev) => {
          const updated = [...prev];
          updated[fileIndex] = { ...updated[fileIndex], status: 'uploading' };
          return updated;
        });

        try {
          await uploadDocumentMutation.mutateAsync(fileStatus.file);

          // Update status to done
          setSelectedFiles((prev) => {
            const updated = [...prev];
            updated[fileIndex] = { ...updated[fileIndex], status: 'done' };
            return updated;
          });
          successCount++;
          return { success: true };
        } catch (error) {
          // Update status to error
          setSelectedFiles((prev) => {
            const updated = [...prev];
            updated[fileIndex] = {
              ...updated[fileIndex],
              status: 'error',
              error: error instanceof Error ? error.message : 'Upload failed',
            };
            return updated;
          });
          errorCount++;
          return { success: false };
        }
      });

      await Promise.allSettled(promises);
    }

    setIsUploading(false);

    // Invalidate documents query to refresh the list
    queryClient.invalidateQueries({ queryKey: ["/api/admin/documents"] });

    // Show summary toast
    if (successCount > 0 && errorCount === 0) {
      toast({
        title: "Upload complete",
        description: `${successCount} ${successCount === 1 ? 'file' : 'files'} uploaded successfully.`,
      });
      // Clear successful files
      setSelectedFiles((prev) => prev.filter((sf) => sf.status === 'error'));
      // Clear input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else if (successCount > 0 && errorCount > 0) {
      toast({
        title: "Upload partially complete",
        description: `${successCount} succeeded, ${errorCount} failed. Failed files are kept for retry.`,
        variant: "destructive",
      });
      // Clear successful files, keep failed ones
      setSelectedFiles((prev) => prev.filter((sf) => sf.status === 'error'));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      toast({
        title: "Upload failed",
        description: `All ${errorCount} ${errorCount === 1 ? 'file' : 'files'} failed to upload.`,
        variant: "destructive",
      });
    }
  };

  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-[#1a1a1a]" />
          <p className="text-[#1a1a1a]">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Show error state if there are critical errors
  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-[#1a1a1a] mx-auto mb-4" />
          <p className="text-[#1a1a1a]">Authentication error: {authError.message}</p>
          <p className="mt-2 text-[#1a1a1a]">Please refresh the page or try logging in again.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  if (!(user?.isAdmin || (user as any)?.is_admin)) {
    return <Redirect to="/" />;
  }

  const handleCreateUser = () => {
    if (!newUsername.trim() || !newPassword.trim()) {
      toast({
        title: "Validation error",
        description: "Username and password are required.",
        variant: "destructive",
      });
      return;
    }
    createUserMutation.mutate({
      username: newUsername,
      password: newPassword,
      firstName: newFirstName.trim() || undefined,
      lastName: newLastName.trim() || undefined,
      isAdmin: newIsAdmin,
      department: newDepartment,
    });
  };

  // Placeholder for the onUploadComplete callback if needed elsewhere
  const handleUploadComplete = () => {
    console.log("Upload completed in DocumentUpload component.");
    // Potentially trigger a refetch or other actions here if needed at the parent level
  };

  return (
    <div 
      className="min-h-screen bg-white text-[#1a1a1a]" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif', 
        background: '#ffffff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#d0d0d0] bg-white sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <GrokLogo size={32} className="flex-shrink-0" />
            <div className="border-l border-[#d0d0d0] pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-lg font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>
                <Shield className="h-5 w-5 text-[#1a1a1a]" />
                <span style={{ textAlign: 'left' }}>Admin</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/project-development">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] border border-[#d0d0d0] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
                title="Project Development"
              >
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                <span className="hidden sm:inline ml-2">Project Development</span>
              </Button>
            </Link>
            <Link href="/">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-4 text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] border border-[#d0d0d0] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <Home className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Chat</span>
                <span className="sm:hidden">Chat</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        {/* Analytics Overview */}
        {!analyticsLoading && analytics && analytics.totalUsers !== undefined && (
          <Collapsible open={isAnalyticsOpen} onOpenChange={setIsAnalyticsOpen}>
            <Card className="mb-8 rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-[#f5f5f5] transition-colors" style={{ textAlign: 'left' }}>
                  <div className="flex items-center justify-between">
                    <div style={{ textAlign: 'left', flex: 1 }}>
                      <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>System Overview</CardTitle>
                      <CardDescription className="text-[#9b9b9b]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                        Monitor system usage, user activity, and platform analytics
                      </CardDescription>
                    </div>
                    <ChevronDown 
                      className={`h-5 w-5 transition-transform duration-200 text-[#1a1a1a] flex-shrink-0 ${
                        isAnalyticsOpen ? "transform rotate-180" : ""
                      }`}
                    />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-[#1a1a1a]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" data-testid="stat-total-users">
                    {analytics.totalUsers}
                  </div>
                  <p className="text-xs mt-1 text-[#666666]">
                    Users
                  </p>
                </CardContent>
              </Card>

                      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]">Conversations</CardTitle>
                  <MessageSquare className="h-4 w-4 text-[#1a1a1a]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" data-testid="stat-total-conversations">
                    {analytics.totalConversations}
                  </div>
                  <p className="text-xs mt-1 text-[#666666]">
                    Conversations
                  </p>
                </CardContent>
              </Card>

                      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-[#1a1a1a]">Messages</CardTitle>
                  <BarChart3 className="h-4 w-4 text-[#1a1a1a]" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-[#1a1a1a]" data-testid="stat-total-messages">
                    {analytics.totalMessages}
                  </div>
                  <p className="text-xs mt-1 text-[#666666]">
                    Messages
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* User Activity Table */}
                    <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-[#1a1a1a]">User Activity</CardTitle>
                <CardDescription className="text-[#1a1a1a]">Track user engagement and platform usage metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]">User</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]">Conversations</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]">Messages</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]">Last Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!analytics?.userActivity || analytics.userActivity.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-[#1a1a1a]">
                          No activity yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      (analytics && Array.isArray(analytics.userActivity) ? analytics.userActivity : []).map((activity) => (
                        <TableRow 
                          key={activity.userId} 
                          data-testid={`activity-row-${activity.userId}`}
                          className="hover:bg-[#f0f0f0] data-[state=selected]:bg-[#e8e8e8]"
                        >
                          <TableCell className="font-medium text-[#1a1a1a] text-sm">{activity.username}</TableCell>
                          <TableCell className="text-right tabular-nums text-[#1a1a1a]" data-testid={`conversation-count-${activity.userId}`}>
                            {activity.conversationCount}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-[#1a1a1a]" data-testid={`message-count-${activity.userId}`}>
                            {activity.messageCount}
                          </TableCell>
                          <TableCell className="text-right text-[#1a1a1a]">
                            {activity.lastActivity
                              ? format(new Date(activity.lastActivity), "MMM d, yyyy")
                              : "Never"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* Cost Usage Overview */}
        <CostUsageOverviewCollapsible isOpen={isCostUsageOpen} onOpenChange={setIsCostUsageOpen} />

        {/* System Prompt Editor */}
        <Collapsible open={isSystemPromptOpen} onOpenChange={setIsSystemPromptOpen}>
          <Card className="mt-8 rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-[#f5f5f5] transition-colors" style={{ textAlign: 'left' }}>
                <div className="flex items-center justify-between">
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>System Prompt</CardTitle>
                    <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                      Configure system instructions to customize AI assistant behavior and responses.
                    </CardDescription>
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform duration-200 text-[#1a1a1a] flex-shrink-0 ${
                      isSystemPromptOpen ? "transform rotate-180" : ""
                    }`}
                  />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="system-prompt" className="text-[#1a1a1a]">System Prompt</Label>
                    {promptLoading ? (
                      <div className="text-sm text-[#1a1a1a]">Loading...</div>
                    ) : (
                      <Textarea
                        id="system-prompt"
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        placeholder="Enter system prompt..."
                        className="min-h-[400px] font-mono text-sm bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                        data-testid="input-system-prompt"
                      />
                    )}
                    <div className="flex items-center justify-between text-xs text-[#1a1a1a]">
                      <span>{systemPrompt.length} characters</span>
                      <span>Max 10,000 characters</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleSavePrompt}
                      disabled={updatePromptMutation.isPending || promptLoading}
                      data-testid="button-save-prompt"
                      className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] rounded-md"
                    >
                      {updatePromptMutation.isPending ? "Saving..." : "Save Prompt"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleResetPrompt}
                      disabled={updatePromptMutation.isPending || promptLoading}
                      data-testid="button-reset-prompt"
                      className="border border-[#d0d0d0] text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]"
                    >
                      Reset to Default
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* User Management */}
        <Collapsible open={isUserManagementOpen} onOpenChange={setIsUserManagementOpen}>
          <Card className="mt-8 rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-[#f5f5f5] transition-colors" style={{ textAlign: 'left' }}>
                <div className="flex items-center justify-between">
              <div style={{ textAlign: 'left', flex: 1 }}>
                <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>User Management</CardTitle>
                    <CardDescription className="text-[#9b9b9b]" style={{ textAlign: 'left', lineHeight: '1.5' }}>Manage user accounts, roles, and permissions</CardDescription>
              </div>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform duration-200 text-[#1a1a1a] flex-shrink-0 ${
                      isUserManagementOpen ? "transform rotate-180" : ""
                    }`}
                  />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <div></div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                      <Button data-testid="button-add-user" className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="border border-[#d0d0d0] bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-[#1a1a1a]">Create User</DialogTitle>
                    <DialogDescription className="text-[#1a1a1a]">
                      Add user access
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-[#1a1a1a]">First Name</Label>
                        <Input
                          id="firstName"
                          value={newFirstName}
                          onChange={(e) => setNewFirstName(e.target.value)}
                          placeholder="First name"
                          className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-sm text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-[#1a1a1a]">Last Name</Label>
                        <Input
                          id="lastName"
                          value={newLastName}
                          onChange={(e) => setNewLastName(e.target.value)}
                          placeholder="Last name"
                          className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-sm text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-[#1a1a1a]">Username</Label>
                      <Input
                        id="username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        placeholder="Enter username"
                        className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-sm text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                        data-testid="input-new-username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-[#1a1a1a]">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter password"
                        className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md px-3 py-2 text-sm text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                        data-testid="input-new-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="department" className="text-[#1a1a1a]">Department</Label>
                      <Select value={newDepartment} onValueChange={setNewDepartment}>
                        <SelectTrigger className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-md text-sm text-[#1a1a1a]">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENT_OPTIONS.map((dept) => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="isAdmin"
                        checked={newIsAdmin}
                        onCheckedChange={setNewIsAdmin}
                        data-testid="switch-is-admin"
                      />
                      <Label htmlFor="isAdmin" className="cursor-pointer text-[#1a1a1a]">
                        Admin privileges
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleCreateUser}
                      disabled={createUserMutation.isPending}
                      data-testid="button-create-user"
                      className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] rounded-md"
                    >
                      {createUserMutation.isPending ? "Creating..." : "Create User"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]">Name</TableHead>
                      <TableHead className="text-[#1a1a1a]">Username</TableHead>
                      <TableHead className="text-[#1a1a1a]">Department</TableHead>
                      <TableHead className="text-[#1a1a1a]">Role</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-[#1a1a1a]">
                          Loading users...
                        </TableCell>
                      </TableRow>
                    ) : sortedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[#1a1a1a]">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedUsers.map((u) => (
                        <TableRow 
                          key={u.id} 
                          data-testid={`row-user-${u.id}`}
                          className="hover:bg-[#f0f0f0] data-[state=selected]:bg-[#e8e8e8]"
                        >
                          <TableCell className="font-medium text-[#1a1a1a] text-sm">
                            <div className="flex items-center gap-2">
                              {u.profilePictureUrl ? (
                                <img
                                  src={u.profilePictureUrl}
                                  alt={`${u.firstName || u.username}`}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-semibold text-gray-600">
                                    {(u.firstName?.[0] || u.lastName?.[0] || u.username[0]).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span>
                                {u.firstName || u.lastName
                                  ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                                  : u.username}
                                {u.id === user.id && (
                                  <span className="text-xs text-[#1a1a1a] ml-1">(You)</span>
                                )}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-[#1a1a1a] text-sm">
                            {u.username}
                          </TableCell>
                          <TableCell>
                            <span className="text-[#1a1a1a]">{u.department || "Sales"}</span>
                          </TableCell>
                          <TableCell>
                            {(u.isAdmin || (u as any).is_admin) ? (
                              <span className="text-[#1a1a1a] font-medium">Admin</span>
                            ) : (
                              <span className="text-[#1a1a1a]">User</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingUser(u);
                                  setEditUsername(u.username);
                                  setEditPassword("");
                                  setEditFirstName(u.firstName || "");
                                  setEditLastName(u.lastName || "");
                                  setEditIsAdmin(!!(u.isAdmin || (u as any).is_admin));
                                  setEditDepartment(u.department || "Sales");
                                  setEditDialogOpen(true);
                                }}
                                disabled={updateUserMutation.isPending}
                                 className="text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] rounded-md"
                                data-testid={`button-edit-user-${u.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteUserMutation.mutate(u.id)}
                                disabled={u.id === user.id || deleteUserMutation.isPending}
                                className="text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]"
                                data-testid={`button-delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
          </CardContent>
            </CollapsibleContent>
        </Card>
        </Collapsible>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="border border-[#d0d0d0] bg-white">
            <DialogHeader>
              <DialogTitle className="text-[#1a1a1a]">Edit User</DialogTitle>
              <DialogDescription className="text-[#1a1a1a]">
                Update user information. Leave password blank to keep the current password.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-firstName" className="text-[#1a1a1a]">First Name</Label>
                  <Input
                    id="edit-firstName"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    placeholder="First name"
                    className="text-[#1a1a1a] border border-[#d0d0d0] bg-[#f7f7f7] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-lastName" className="text-[#1a1a1a]">Last Name</Label>
                  <Input
                    id="edit-lastName"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    placeholder="Last name"
                    className="text-[#1a1a1a] border border-[#d0d0d0] bg-[#f7f7f7] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-username" className="text-[#1a1a1a]">Username</Label>
                <Input
                  id="edit-username"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  placeholder="Enter username"
                  className="text-[#1a1a1a] border border-[#d0d0d0] bg-[#f7f7f7] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                  data-testid="input-edit-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password" className="text-[#1a1a1a]">Password</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  placeholder="Leave blank to keep current password"
                  className="text-[#1a1a1a] border border-[#d0d0d0] bg-[#f7f7f7] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
                  data-testid="input-edit-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-department" className="text-[#1a1a1a]">Department</Label>
                <Select value={editDepartment} onValueChange={setEditDepartment}>
                  <SelectTrigger className="bg-[#f7f7f7] border border-[#d0d0d0] rounded-md text-sm text-[#1a1a1a]">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENT_OPTIONS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-isAdmin"
                  checked={editIsAdmin}
                  onCheckedChange={setEditIsAdmin}
                  data-testid="switch-edit-is-admin"
                />
                <Label htmlFor="edit-isAdmin" className="cursor-pointer text-[#1a1a1a]">
                  Admin privileges
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingUser(null);
                  setEditUsername("");
                  setEditPassword("");
                  setEditIsAdmin(false);
                  setEditDepartment("Sales");
                }}
                className="border border-[#d0d0d0] text-[#1a1a1a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!editingUser) return;
                  if (!editUsername.trim()) {
                    toast({
                      title: "Validation error",
                      description: "Username is required.",
                      variant: "destructive",
                    });
                    return;
                  }
                  updateUserMutation.mutate({
                    id: editingUser.id,
                    username: editUsername.trim(),
                    password: editPassword.trim() || undefined,
                    firstName: editFirstName.trim() || undefined,
                    lastName: editLastName.trim() || undefined,
                    isAdmin: editIsAdmin,
                    department: editDepartment,
                  });
                }}
                disabled={updateUserMutation.isPending || !editUsername.trim()}
                className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
                data-testid="button-update-user"
              >
                {updateUserMutation.isPending ? "Updating..." : "Update User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Library */}
        <Collapsible open={isDocumentLibraryOpen} onOpenChange={setIsDocumentLibraryOpen}>
          <Card className="mt-8 rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-[#f5f5f5] transition-colors" style={{ textAlign: 'left' }}>
                <div className="flex items-center justify-between">
                  <div style={{ textAlign: 'left', flex: 1 }}>
            <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>Knowledge Base Management</CardTitle>
                    <CardDescription className="text-[#9b9b9b]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
              Manage and organize your document repository. Upload guidelines, exceptions, and reference materials for the AI assistant.
            </CardDescription>
                  </div>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform duration-200 text-[#1a1a1a] flex-shrink-0 ${
                      isDocumentLibraryOpen ? "transform rotate-180" : ""
                    }`}
                  />
                </div>
          </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
          <CardContent>
            <div className="space-y-6">
              {/* Upload Section */}
                  <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
                  <CardHeader>
                  <CardTitle className="text-[#1a1a1a]">Document Upload</CardTitle>
                  <CardDescription className="text-[#1a1a1a]">
                    Upload documents to enhance the knowledge base. Select document type: Guidelines (strict policy rules) or Exceptions (flexible criteria).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DocumentUpload onUploadComplete={handleUploadComplete} />
                </CardContent>
              </Card>

              {/* Documents List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#1a1a1a]">Document Repository ({(documents || []).length})</h3>
                  <div className="flex items-center gap-2">
                    <ReprocessAllButton 
                      documentsCount={(documents || []).length}
                      onProcessingChange={(isProcessing) => {
                        // Set all documents as processing when reprocess all is triggered
                        if (isProcessing) {
                          setDocumentProcessingStates(prev => {
                            const newStates = { ...prev };
                            documents?.forEach(doc => {
                              newStates[doc.id] = true;
                            });
                            return newStates;
                          });
                        } else {
                          setDocumentProcessingStates({});
                        }
                      }}
                    />
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]">Filename</TableHead>
                      <TableHead className="text-[#1a1a1a]">Tags</TableHead>
                      <TableHead className="text-[#1a1a1a]">Size</TableHead>
                      <TableHead className="text-[#1a1a1a]">Status</TableHead>
                      <TableHead className="text-[#1a1a1a]">Uploaded</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#1a1a1a]">
                          Loading documents...
                        </TableCell>
                      </TableRow>
                    ) : (documents || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#1a1a1a] py-8">
                          <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                          <p className="text-[#1a1a1a]">No documents in repository</p>
                          <p className="text-xs mt-1 text-[#1a1a1a]">Upload documents to expand the knowledge base and improve response accuracy</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (documents && Array.isArray(documents) ? documents : []).map((doc) => {
                        // Add safety check for doc properties
                        if (!doc || typeof doc !== 'object') {
                          console.error('Invalid document object:', doc);
                          return null;
                        }
                        return (
                        <TableRow 
                          key={doc.id} 
                          data-testid={`row-document-${doc.id}`}
                          className="hover:bg-[#f0f0f0] data-[state=selected]:bg-[#e8e8e8]"
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#1a1a1a] text-sm">{doc.filename}</span>
                              <Badge 
                                variant="outline" 
                                      className="border-[#d0d0d0] text-[#1a1a1a] bg-white"
                              >
                                {doc.documentType === 'guideline' ? 'Guideline' : 'Exception'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <DocumentTagsEditor documentId={doc.id} initialTags={doc.tags} />
                          </TableCell>
                          <TableCell className="text-[#1a1a1a] tabular-nums">
                            {(doc.contentLength / 1024).toFixed(1)} KB
                          </TableCell>
                          <TableCell>
                            <DocumentStatusBadge 
                              chunkCount={doc.chunkCount}
                              isProcessed={doc.isProcessed}
                              isProcessing={documentProcessingStates[doc.id] || false}
                            />
                          </TableCell>
                          <TableCell className="text-[#1a1a1a]">
                            {doc.uploadedAt ? format(new Date(doc.uploadedAt), "MMM d, yyyy") : "No date"}
                          </TableCell>
                          <TableCell className="text-right">
                            <DocumentActions 
                              documentId={doc.id} 
                              filename={doc.filename}
                              onProcessingChange={(isProcessing) => {
                                setDocumentProcessingStates(prev => ({
                                  ...prev,
                                  [doc.id]: isProcessing
                                }));
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        );
                      }).filter(Boolean)
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
            </CollapsibleContent>
        </Card>
        </Collapsible>

        {/* Response Hub Link */}
        <Card className="mt-8 rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
          <CardHeader style={{ textAlign: 'left' }}>
            <CardTitle className="flex items-center gap-2 text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px' }}>
              <MessageSquare className="h-5 w-5" />
              Response Hub
            </CardTitle>
            <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
              Review responses, see feedback and approve or edit answers
            </CardDescription>
          </CardHeader>
          <CardContent style={{ textAlign: 'left' }}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div style={{ textAlign: 'left', flex: 1 }}>
                <p className="text-sm text-[#1a1a1a] mb-3" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                  Access the Response Hub to review cached responses, user ratings, and comments.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-[#1a1a1a]" style={{ textAlign: 'left' }}>
                  <span style={{ textAlign: 'left' }}>• View all AI responses</span>
                  <span style={{ textAlign: 'left' }}>• Manage user feedback</span>
                  <span style={{ textAlign: 'left' }}>• Approve/deny responses</span>
                  <span style={{ textAlign: 'left' }}>• Add admin comments</span>
                </div>
              </div>
              <Link href="/response-hub">
                 <Button className="flex items-center gap-2 border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] rounded-md touch-manipulation" style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}>
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Open Response Hub</span>
                  <span className="sm:hidden">Open</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>


      </main>
    </div>
  );
}